import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../common/icons';
import { useA11yPrefs, useSpeechAvailable } from './a11y';
import './testShell.css';

const tx = (lang, en, fil) => (lang === 'fil' ? fil : en);

const Switch = ({ on, onChange, icon, label, hint }) => (
  <button type="button" role="switch" aria-checked={on} className={`a11y-row${on ? ' is-on' : ''}`} onClick={() => onChange(!on)}>
    <span className="a11y-ic" aria-hidden="true">
      <Icon name={icon} size={20} />
    </span>
    <span className="a11y-text">
      <b>{label}</b>
      <small>{hint}</small>
    </span>
    <span className="a11y-switch" aria-hidden="true">
      <i />
    </span>
  </button>
);

/**
 * The accessibility button in the chat header: Larger text and Read questions aloud, remembered
 * on this device. Read aloud only shows where the server can do it (Gemini voice).
 */
const A11yMenu = ({ language = 'en' }) => {
  const lang = language === 'fil' ? 'fil' : 'en';
  const [prefs, update] = useA11yPrefs();
  const speech = useSpeechAvailable();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        rootRef.current?.querySelector('.a11y-toggle')?.focus();
      }
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const active = prefs.largeText || (speech && prefs.readAloud);
  return (
    <div className="a11y-menu" ref={rootRef}>
      <button
        type="button"
        className={`a11y-toggle${open ? ' is-open' : ''}${active ? ' is-active' : ''}`}
        aria-label={tx(lang, 'Accessibility', 'Accessibility')}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon name="accessibility" size={22} />
      </button>
      {open && (
        <div className="a11y-pop" role="group" aria-label={tx(lang, 'Accessibility', 'Accessibility')}>
          <span className="a11y-title">{tx(lang, 'Make it easier', 'Gawing mas madali')}</span>
          <Switch
            on={prefs.largeText}
            onChange={(on) => update({ largeText: on })}
            icon="text-size"
            label={tx(lang, 'Larger text', 'Mas malaking text')}
            hint={tx(lang, 'Bigger words and buttons', 'Mas malalaking salita at button')}
          />
          {speech && (
            <Switch
              on={prefs.readAloud}
              onChange={(on) => update({ readAloud: on })}
              icon="volume"
              label={tx(lang, 'Read questions aloud', 'Basahin nang malakas ang tanong')}
              hint={tx(lang, "Anna reads each question as it comes", 'Babasahin ni Anna ang bawat tanong')}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default A11yMenu;
